/** English dictionary — the source of truth for translation keys. Placeholders use {name} syntax. */

import { storageEn } from './screens/storage'
import { sharingEn } from './screens/sharing'
import { secretsEn } from './screens/secrets'
import { billingEn } from './screens/billing'
import { searchAnalyticsEn } from './screens/search-analytics'
import { wikiCompileEn } from './screens/wiki-compile'
import { healthEn } from './screens/health'
import { askEn } from './screens/ask'
import { sourcesTabsEn } from './screens/sources-tabs'
import { savedQuestionsEn } from './screens/saved-questions'
import { setupEn } from './screens/setup'

export const en = {
  ...storageEn,
  ...sharingEn,
  ...secretsEn,
  ...billingEn,
  ...searchAnalyticsEn,
  ...wikiCompileEn,
  ...healthEn,
  ...askEn,
  ...sourcesTabsEn,
  ...savedQuestionsEn,
  ...setupEn,
  'app.tagline': 'Knowledge management for humans & AI agents',

  'common.loading': 'Loading…',
  'common.retry': 'Retry',
  'common.cancel': 'Cancel',
  'common.close': 'Close',

  'login.continueWithGoogle': 'Continue with Google',
  'login.continueWithGithub': 'Continue with GitHub',
  'login.terms': 'By continuing you agree to our terms of service and privacy policy.',

  'theme.label': 'Theme',
  'theme.light': 'Light',
  'theme.dark': 'Dark',
  'theme.system': 'System',

  'language.label': 'Language',
  'language.en': 'English',
  'language.vi': 'Tiếng Việt',

  'nav.projects': 'Projects',
  'nav.overview': 'Overview',
  'nav.wiki': 'Wiki',
  'nav.sources': 'Sources',
  'nav.workspace': 'Workspace',
  'nav.documents': 'Documents',
  'nav.storage': 'Storage',
  'nav.sites': 'Hosted sites',
  'nav.secrets': 'Secrets',
  'nav.searchAnalytics': 'Search analytics',
  'nav.graph': 'Knowledge graph',
  'nav.settings': 'Settings',
  'nav.billing': 'Billing',
  'nav.profile': 'Profile',

  'shell.openMenu': 'Open navigation',
  'shell.closeMenu': 'Close navigation',
  'shell.newProject': 'New project',
  'shell.projectsError': 'Could not load projects.',
  'shell.noProjects': 'No projects yet.',
  'shell.defaultProject': 'default',

  'topbar.search': 'Search or run a command',
  'topbar.switchProject': 'Switch project',
  'topbar.userMenu': 'Account menu',
  'topbar.signOut': 'Sign out',

  'project.create.title': 'Create project',
  'project.create.name': 'Name',
  'project.create.namePlaceholder': 'e.g. Payments service',
  'project.create.slug': 'Slug (optional)',
  'project.create.slugHint': 'Lowercase letters, digits, and dashes. Generated from the name when empty.',
  'project.create.description': 'Description (optional)',
  'project.create.repoUrl': 'Repository URL (optional)',
  'project.create.submit': 'Create project',
  'project.create.submitting': 'Creating…',
  'project.create.nameRequired': 'Name is required.',
  'project.create.slugInvalid': 'Slug must use lowercase letters, digits, and dashes, and cannot start or end with a dash.',
  'project.create.slugTaken': 'The slug "{slug}" is already used by another project.',
  'project.create.nameTaken': 'A project with a slug generated from this name already exists. Enter a different slug.',
  'project.create.failed': 'Could not create the project: {message}',
  'project.create.created': 'Project "{name}" created',

  'project.overview.sources': 'Sources',
  'project.overview.pages': 'Wiki pages',
  'project.overview.noDescription': 'No description yet.',
  'project.overview.repository': 'Repository',
  'project.overview.defaultProject': 'Default project',
  'project.overview.updated': 'Updated {date}',
  'project.overview.notFound': 'Project "{slug}" was not found.',
  'project.overview.loadError': 'Could not load this project.',
  'project.overview.backToProjects': 'Go to the default project',
  'project.overview.open': 'Open',

  'palette.projects': 'Projects',
  'palette.workspace': 'Go to',
  'palette.preferences': 'Preferences',
  'palette.createProject': 'Create project',
  'palette.setTheme': 'Theme: {theme}',
  'palette.setLanguage': 'Language: {language}',

  'settings.preferences': 'Preferences',
  'settings.themeHint': 'System follows your operating system setting.',
  'settings.languageHint': 'Applies to the new app shell, sign-in page, and preference controls.',

  'sources.title': 'Sources',
  'sources.subtitle': 'Raw material pushed by agents, the CLI, or this page. Wiki pages cite these.',
  'sources.upload': 'Upload files',
  'sources.uploading': 'Uploading…',
  'sources.search': 'Search title or filename',
  'sources.status.active': 'Active',
  'sources.status.superseded': 'Replaced',
  'sources.status.archived': 'Archived',
  'sources.status.all': 'All',
  'sources.empty': 'No sources yet. Upload files here or push them from your repo:',
  'sources.col.file': 'File',
  'sources.col.origin': 'Origin',
  'sources.col.size': 'Size',
  'sources.col.added': 'Added',
  'sources.uploaded': '{count} file(s) uploaded',
  'sources.unchanged': '{name} is unchanged',
  'sources.uploadFailed': 'Could not upload {name}: {message}',
  'sources.notText': '{name} is not a text file',
  'sources.loadError': 'Could not load sources.',
  'sources.count': '{shown} of {total}',
  'source.back': 'All sources',
  'source.replacedBy': 'Replaced by a newer version',
  'source.openNewer': 'Open newer version',
  'source.archivedNote': 'Archived: kept with its citations, but compile, context, and Ask no longer read it.',
  'source.archive': 'Archive',
  'source.restore': 'Restore',
  'source.archived': 'Source archived',
  'source.restored': 'Source restored',
  'source.archiveFailed': 'Could not change the source: {message}',
  'source.notFound': 'Source not found.',
  'wiki.title': 'Wiki',
  'wiki.subtitle': 'Pages compiled from Sources. Every sentence links back to where it came from.',
  'wiki.empty': 'No compiled pages yet. Ask your coding agent to compile the Sources:',
  'wiki.untopiced': 'Other',
  'wiki.cited': '{percent}% cited',
  'wiki.version': 'v{version}',
  'wiki.loadError': 'Could not load wiki pages.',
  'wiki.contents': 'Contents',
  'wiki.citedSources': 'Cited sources',
  'wiki.changes': 'Change log',
  'wiki.back': 'All pages',
  'wiki.notFound': 'Page not found.',
  'context.title': 'Context preview',
  'context.hint': 'What an agent gets from `agentwiki context` for a task.',
  'context.task': 'Task',
  'context.taskPlaceholder': 'e.g. add a D1 migration for a new table',
  'context.budget': 'Token budget',
  'context.includeSources': 'Include raw sources',
  'context.run': 'Build context',
  'context.running': 'Building…',
  'context.result': '{items} item(s), about {used} of {budget} tokens',
  'context.failed': 'Could not build context: {message}',

  'nav.review': 'Review',
  'review.title': 'Review queue',
  'review.subtitle': 'Changes agents proposed for the wiki. Add a change to publish it, or discard it.',
  'review.pending': '{count} waiting',
  'review.empty': 'Nothing waiting for review.',
  'review.loadError': 'Could not load proposals.',
  'review.status.pending': 'Waiting',
  'review.status.applied': 'Added',
  'review.status.discarded': 'Discarded',
  'review.status.all': 'All',
  'review.newPage': 'New page',
  'review.basedOn': 'Based on v{version}',
  'review.by.agent': 'Agent',
  'review.by.user': 'Person',
  'review.back': 'Review queue',
  'review.notFound': 'Proposal not found.',
  'review.checks': 'Checks',
  'review.check.citations': '{cited} of {total} sentences cite a source ({percent}%)',
  'review.check.superseded': 'Cites replaced sources: {ids}',
  'review.check.stale': 'The page changed to v{current} after this proposal was based on v{base}. Ask the agent to redo it on the latest version.',
  'review.check.pinned': 'Changes pinned sections: {anchors}',
  'review.check.contradiction': 'Possible contradiction ({reason})',
  'review.reason.number': 'different numbers',
  'review.reason.negation': 'negation flipped',
  'review.check.ok': 'No blocking issues.',
  'review.diff': 'Changes',
  'review.noDiff': 'No line changes.',
  'review.note': 'Note (optional)',
  'review.add': 'Add to wiki',
  'review.discard': 'Discard',
  'review.working': 'Saving…',
  'review.added': 'Change added to the wiki',
  'review.discarded': 'Proposal discarded',
  'review.failed': 'Could not save the decision: {message}',
  'review.decided': '{status} by {by}',
  'wiki.pin': 'Pin section',
  'wiki.unpin': 'Unpin section',
  'wiki.pinned': 'Pinned: agents cannot change this section',
  'wiki.pinFailed': 'Could not update pins: {message}',
  'settings.save': 'Save',
  'settings.saving': 'Saving…',
  'settings.saved': 'Saved',
  'settings.edit': 'Edit',
  'settings.remove': 'Remove',
  'settings.dismiss': 'Dismiss',
  'settings.loadError': 'Could not load this section.',
  'settings.saveFailed': 'Could not save: {message}',
  'settings.apiKeys.title': 'API keys',
  'settings.apiKeys.subtitle': 'Keys for the CLI, MCP, and scripts. Each key only gets the scopes you pick.',
  'settings.apiKeys.create': 'Create API key',
  'settings.apiKeys.newKey': 'New API key',
  'settings.apiKeys.name': 'Name',
  'settings.apiKeys.namePlaceholder': 'e.g. Laptop CLI',
  'settings.apiKeys.scopes': 'Scopes',
  'settings.apiKeys.selectAll': 'Select all',
  'settings.apiKeys.deselectAll': 'Clear all',
  'settings.apiKeys.agentFlags': 'Agent secret access (MCP)',
  'settings.apiKeys.agentFlagsHint': 'Lets an agent pull raw secret values into its context. Grant only to keys that need it; reveals still need your approval on the web.',
  'settings.apiKeys.expiresIn': 'Expires in (days, optional)',
  'settings.apiKeys.newExpiry': 'New expiry in days (leave blank to keep the current one)',
  'settings.apiKeys.removeExpiry': 'Remove expiry (never expires)',
  'settings.apiKeys.createSubmit': 'Create key',
  'settings.apiKeys.creating': 'Creating…',
  'settings.apiKeys.createFailed': 'Could not create the key: {message}',
  'settings.apiKeys.updateFailed': 'Could not update the key: {message}',
  'settings.apiKeys.saveNow': 'Copy this key now. It will not be shown again.',
  'settings.apiKeys.copy': 'Copy key',
  'settings.apiKeys.empty': 'No API keys yet.',
  'settings.apiKeys.editKey': 'Edit key',
  'settings.apiKeys.editNamed': 'Edit {name}',
  'settings.apiKeys.revoke': 'Revoke',
  'settings.apiKeys.revokeConfirm': 'Revoke the key "{name}"? Tools using it stop working immediately.',
  'settings.apiKeys.created': 'Created {date}',
  'settings.apiKeys.lastUsed': 'Last used {date}',
  'settings.apiKeys.expires': 'Expires {date}',
  'settings.members.title': 'Members',
  'settings.members.subtitle': 'People in this workspace and their roles.',
  'settings.members.invite': 'Invite member',
  'settings.members.inviteHint': 'The person must have signed in to AgentWiki once before you can add them.',
  'settings.members.email': 'Email',
  'settings.members.roleLabel': 'Role',
  'settings.members.sendInvite': 'Add member',
  'settings.members.inviting': 'Adding…',
  'settings.members.invited': 'Member added',
  'settings.members.empty': 'No members yet.',
  'settings.members.roleConfirm': 'Change {name}\'s role to {role}?',
  'settings.members.removeConfirm': 'Remove {name} from this workspace?',
  'settings.members.roleFor': 'Role of {name}',
  'settings.members.removeNamed': 'Remove {name}',
  'settings.members.role.admin': 'Admin',
  'settings.members.role.editor': 'Editor',
  'settings.members.role.viewer': 'Viewer',
  'settings.members.role.agent': 'Agent',
  'settings.ai.title': 'AI providers',
  'settings.ai.subtitle': 'Keys for server-side AI features such as summaries and graph extraction. Drag to set the fallback order. Keys are encrypted at rest.',
  'settings.ai.configured': 'Configured ({count})',
  'settings.ai.available': 'Available ({count})',
  'settings.ai.configure': 'Configure',
  'settings.ai.apiKey': 'API key',
  'settings.ai.enterKey': 'Paste the provider API key',
  'settings.ai.keySaved': 'Saved. Leave blank to keep it.',
  'settings.ai.model': 'Model',
  'settings.ai.enabled': 'Enabled',
  'settings.ai.active': 'Active',
  'settings.ai.disabled': 'Disabled',
  'settings.ai.dragToReorder': 'Drag to reorder {name}',
  'settings.ai.expand': 'Edit {name}',
  'settings.ai.collapse': 'Close {name}',
  'settings.ai.removeConfirm': 'Remove {name}?',
  'settings.ai.usage': 'Usage',
  'settings.ai.noUsage': 'No AI usage recorded yet.',
  'settings.ai.col.provider': 'Provider',
  'settings.ai.col.model': 'Model',
  'settings.ai.col.action': 'Action',
  'settings.ai.col.tokens': 'Tokens',
  'settings.ai.col.date': 'Date',
  'settings.storage.title': 'Storage',
  'settings.storage.summary': '{count} file(s) · {size}',
  'settings.storage.upload': 'Upload file',
  'settings.storage.uploading': 'Uploading…',
  'settings.storage.connected': 'Connected',
  'settings.storage.bucket': 'Bucket',
  'settings.storage.maxFileSize': 'Max file size',
  'settings.storage.totalFiles': 'Files',
  'settings.storage.totalSize': 'Total size',
  'settings.storage.r2Hint': 'The default bucket is managed in the Cloudflare dashboard. Images added to documents are uploaded here.',
  'settings.storage.empty': 'No files uploaded yet.',
  'settings.storage.uploadFirst': 'Upload your first file',
  'settings.storage.previewNamed': 'Preview {name}',
  'settings.storage.downloadNamed': 'Download {name}',
  'settings.storage.deleteNamed': 'Delete {name}',
  'settings.storage.deleteConfirm': 'Delete "{name}"?',
  'settings.storage.config.title': 'Custom R2 / S3 storage',
  'settings.storage.config.verified': 'Verified',
  'settings.storage.config.notVerified': 'Not verified',
  'settings.storage.config.default': 'Default',
  'settings.storage.config.hide': 'Hide',
  'settings.storage.config.accountId': 'Account ID',
  'settings.storage.config.bucketName': 'Bucket name',
  'settings.storage.config.endpointUrl': 'Endpoint URL (optional, for S3-compatible storage other than R2)',
  'settings.storage.config.accessKey': 'Access key ID',
  'settings.storage.config.secretKey': 'Secret access key',
  'settings.storage.config.test': 'Test connection',
  'settings.storage.config.testOk': 'Connection works.',
  'settings.storage.config.testFailed': 'Connection failed.',
  'settings.storage.config.removeConfirm': 'Remove the custom storage settings and go back to the default bucket?',
  'nav.project': 'Project',
  'nav.allProjects': 'All projects',
  'nav.docs': 'Docs',
  'nav.apiDocs': 'API reference',
  'nav.cliDocs': 'CLI reference',
  'nav.opensInNewTab': 'opens in a new tab',
  'shell.navigation': 'Main navigation',
  'shell.breadcrumb': 'Breadcrumb',
  'shell.currentProject': 'Current project',
  'documents.title': 'Documents',
  'documents.subtitle': 'Pages you and your team write by hand. Agents read them through search and context.',
  'documents.new': 'New document',
  'documents.search': 'Search by title',
  'documents.projectFilter': 'Filter by project',
  'documents.allProjects': 'All projects',
  'documents.loadError': 'Could not load documents.',
  'documents.empty': 'No documents yet.',
  'documents.noMatch': 'No documents match this search.',
  'documents.pagination': 'Pages',
  'documents.range': '{from}–{to} of {total}',
  'documents.previous': 'Previous',
  'documents.next': 'Next',
  'documents.accessLevel': 'Who can see it',
  'documents.access.private': 'Only me',
  'documents.access.specific': 'Specific people',
  'documents.access.public': 'Public (published)',
  'documents.create.title': 'New document',
  'documents.create.docTitle': 'Title',
  'documents.create.description': 'Description',
  'documents.create.descriptionHint': 'One or two sentences. Search and agents use it to decide when this page is relevant.',
  'documents.create.required': 'Title and description are required.',
  'documents.create.submit': 'Create document',
  'documents.create.submitting': 'Creating…',
  'documents.create.failed': 'Could not create the document: {message}',
  'document.back': 'All documents',
  'document.notFound': 'This document does not exist or you cannot see it.',
  'document.loadError': 'Could not load this document.',
  'document.edit': 'Edit',
  'document.convert': 'Convert to markdown',
  'document.properties': 'Properties',
  'document.updated': 'Updated {date}',
  'document.author': 'by {name}',
  'document.empty': 'This page is empty.',
  'document.lossy.notice': 'This page was written in the old rich editor and uses formatting markdown cannot keep ({features}). It is read-only here so nothing is lost. Converting it to markdown makes it editable.',
  'document.lossy.color': 'text or background colors',
  'document.lossy.underline': 'underline',
  'document.lossy.alignment': 'centered or right-aligned text',
  'document.lossy.indentation': 'indented blocks',
  'document.lossy.media': 'video, audio, or file blocks',
  'document.lossy.imageSize': 'resized images',
  'document.lossy.tableLayout': 'table column widths',
  'document.lossy.unknownBlock': 'blocks markdown has no form for',
  'document.convertTitle': 'Convert this page to markdown?',
  'document.convertBody': 'When you save, the page is stored as markdown only and these are dropped: {features}. Text, headings, lists, links, code, quotes, tables, and images stay.',
  'document.convertSnapshot': 'Before saving, a read-only snapshot of the current version is kept in version history. Nothing changes until you press Save.',
  'document.convertConfirm': 'Continue to edit',
  'document.titleLabel': 'Title',
  'document.titleRequired': 'Title is required.',
  'document.contentLabel': 'Content (markdown)',
  'document.editorMode': 'Editor mode',
  'document.write': 'Write',
  'document.preview': 'Preview',
  'document.markdownHint': 'Markdown. Ctrl/⌘ + S to save.',
  'document.saveFailed': 'Could not save: {message}',
  'document.discardConfirm': 'Discard your unsaved changes?',
  'projects.title': 'Projects',
  'projects.subtitle': 'Each project has its own Sources, compiled wiki, and review queue. Usually one per repository.',
  'projects.edit': 'Edit project',
  'projects.editNamed': 'Edit {name}',
  'projects.editTitle': 'Edit project',
  'projects.saved': 'Project "{name}" saved',
  'projects.slugFixed': 'The slug "{slug}" stays the same, so CLI and MCP links keep working.',
  'projects.sources': '{count} sources',
  'projects.pages': '{count} wiki pages',
  'onboarding.welcome': 'Welcome to AgentWiki',
  'onboarding.welcomeNamed': 'Welcome, {name}',
  'onboarding.intro': 'Start with a project: the place where your agents push sources and AgentWiki keeps the compiled wiki. One project per repository works well.',
  'onboarding.choose': 'How do you want to start?',
  'onboarding.useExisting': 'Use the project you have',
  'onboarding.useExistingHint': 'Your workspace already has a project. Give it a name that fits.',
  'onboarding.createNew': 'Create a new project',
  'onboarding.createNewHint': 'Start fresh, for example for one repository.',
  'onboarding.projectName': 'Project name',
  'onboarding.descriptionPlaceholder': 'What this project covers',
  'onboarding.laterHint': 'You can rename projects and add more later.',
  'onboarding.continue': 'Continue',
  'onboarding.skip': 'Skip for now',
  'onboarding.working': 'Saving…',
  'onboarding.failed': 'Something went wrong: {message}',
  'settings.tab.profile': 'Profile',
  'settings.tab.workspace': 'Workspace',
  'settings.tab.graphAi': 'Graph AI',
  'settings.tab.import': 'Import',
  'settings.profile.subtitle': 'How you appear to your team.',
  'settings.profile.displayName': 'Display name',
  'settings.profile.signOutHint': 'Sign out of AgentWiki on this device.',
  'settings.workspace.credits': 'Credit balance',
  'settings.workspace.creditCount': '{count} credits',
  'settings.workspace.manageBilling': 'Manage billing',
  'document.delete': 'Delete',
  'document.deleteConfirm': 'Delete "{title}"?',
  'document.deleted': '"{title}" deleted',
  'document.deleteFailed': 'Could not delete: {message}',
  'document.descriptionRequired': 'Description cannot be empty.',
  'document.convertFailed': 'Could not load this page from the old editor format. Nothing was changed.',
  'document.renamePublishedKeepsLink': 'This page is published. Its public link keeps its current address after the rename.',
  'document.conflictTitle': 'This page changed while you were editing',
  'document.conflictBody': 'Someone else (or an agent) saved a newer version. Your changes were not saved and are still in the editor; copy anything you want to keep.',
  'document.conflictLoadLatest': 'Discard my changes and load the latest version',
  'document.conflictDiscardConfirm': 'Discard your unsaved changes and load the latest version?',
  'document.conflictCopyDraft': 'Copy my draft',
  'document.conflictDraftCopied': 'Your draft is on the clipboard.',
  'document.conflictCopyFailed': 'Could not copy. Select the text in the editor and copy it.',
} satisfies Record<string, string>

export type TranslationKey = keyof typeof en

/** Every locale must provide exactly the keys of the English dictionary */
export type Dictionary = Record<TranslationKey, string>
